import moment from "moment";
import { DateRangePreset } from "../../../types/reports";

/**
 * Client-side resolver for the reports `DateRangePreset` values → inclusive
 * [start, end] moments. The reports API resolves presets server-side, but the
 * Sales `/sales` endpoint needs explicit dates, so the Payments tab resolves
 * here before querying. Mirrors the reports preset semantics.
 */
export function resolveReportsRange(
  preset: DateRangePreset,
  customStart: string | null,
  customEnd: string | null,
): [moment.Moment, moment.Moment] {
  const now = moment();
  const endToday = now.clone().endOf("day");

  switch (preset) {
    case "today":
      return [now.clone().startOf("day"), endToday];
    case "yesterday":
      return [
        now.clone().subtract(1, "day").startOf("day"),
        now.clone().subtract(1, "day").endOf("day"),
      ];
    case "last_7_days":
      return [now.clone().subtract(6, "days").startOf("day"), endToday];
    case "last_14_days":
      return [now.clone().subtract(13, "days").startOf("day"), endToday];
    case "last_30_days":
      return [now.clone().subtract(29, "days").startOf("day"), endToday];
    case "last_year_rolling":
      return [now.clone().subtract(1, "year").startOf("day"), endToday];
    case "this_week":
      return [now.clone().startOf("week"), endToday];
    case "last_week":
      return [
        now.clone().subtract(1, "week").startOf("week"),
        now.clone().subtract(1, "week").endOf("week"),
      ];
    case "this_month":
      return [now.clone().startOf("month"), endToday];
    case "last_month":
      return [
        now.clone().subtract(1, "month").startOf("month"),
        now.clone().subtract(1, "month").endOf("month"),
      ];
    case "quarter_to_date":
      return [now.clone().startOf("quarter"), endToday];
    case "this_year":
      return [now.clone().startOf("year"), endToday];
    case "last_year":
      return [
        now.clone().subtract(1, "year").startOf("year"),
        now.clone().subtract(1, "year").endOf("year"),
      ];
    case "fixed_dates":
      return [
        customStart
          ? moment(customStart, "YYYY-MM-DD").startOf("day")
          : now.clone().subtract(29, "days").startOf("day"),
        customEnd ? moment(customEnd, "YYYY-MM-DD").endOf("day") : endToday,
      ];
    default:
      return [now.clone().subtract(29, "days").startOf("day"), endToday];
  }
}

/** True once a custom range has both endpoints (safe to query). */
export function reportsRangeReady(
  preset: DateRangePreset,
  customStart: string | null,
  customEnd: string | null,
): boolean {
  return preset !== "fixed_dates" || (!!customStart && !!customEnd);
}
